import { useMemo, type ComponentType } from 'react';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useTopology from '@api/hooks/topology/useTopology';
import { useFlagSelector } from '@hooks/useLayoutOptions';
import { isWebView } from '@utils/deviceHelper';
import Dynamic from '@base/Dynamic/Dynamic';

type SalesBotWidgetProps = {
  goalUid?: string;
  page?: string;
  unreadCount?: number;
};

const SalesBotWidget = Dynamic(() => import('./SalesBotWidget'), {
  ssr: false
}) as ComponentType<SalesBotWidgetProps>;

type GlobalSalesBotFabProps = {
  inEducatorSide?: boolean;
};

/**
 * Global host entry for the sales bot FAB + iframe panel.
 * Shown for logged-in free users when the goal has `is_sales_bot_enabled`
 * from topology `/self/` and other host gates pass.
 */
const GlobalSalesBotFab = ({
  inEducatorSide = false
}: GlobalSalesBotFabProps) => {
  const router = useRouter();
  const goalUid = useSelectedGoalUID();
  const { topology } = useTopology(goalUid);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isSubscribedToGoal = useMyInfo(getIsSubscribedToGoal(goalUid));
  const hideSalesBot = useFlagSelector('hideSalesBot');
  const isSalesBotEnabled = topology?.isSalesBotEnabled === true;

  const shouldHide = useMemo(() => {
    if (!isLoggedIn) return true;
    if (!goalUid) return true;
    if (!isSalesBotEnabled) return true;
    if (isSubscribedToGoal) return true;
    if (inEducatorSide) return true;
    if (hideSalesBot) return true;
    if (router.pathname?.startsWith('/embed/sales-bot')) return true;
    if (router.query?.inside_app === 'true') return true;
    if (typeof window !== 'undefined' && isWebView({})) return true;
    return false;
  }, [
    goalUid,
    hideSalesBot,
    inEducatorSide,
    isLoggedIn,
    isSalesBotEnabled,
    isSubscribedToGoal,
    router.pathname,
    router.query?.inside_app
  ]);

  if (shouldHide) return null;

  const page =
    typeof router.query?.page === 'string' ? router.query.page : undefined;

  return <SalesBotWidget goalUid={goalUid} page={page} />;
};

export default GlobalSalesBotFab;
